import * as THREE from 'three';

export function updateLegsVisibility(model, showNormalLegs) {
  if (!model) return;
  
  model.traverse((child) => {
    if (child.name === 'Legs') {
      child.visible = showNormalLegs;
    }
    if (child.name === 'legs_transparent' || 
        child.name.toLowerCase().includes('legs_transparent')) {
      child.visible = !showNormalLegs;
    }
  });
}

export function handleAnimation(
  currentStep, 
  camera, 
  glassesModel, 
  indicatorRef,
  updateIndicatorPosition,
  animationPhaseRef
) {
  if (!camera || !glassesModel) return;
  
  if (indicatorRef.current) {
    indicatorRef.current.style.opacity = '0';
  }
  
  if (currentStep < 40) {
    animationPhaseRef.current = 'zoom';
    
    const startCameraZ = 3;
    const targetCameraZ = 50;
    const zoomProgress = currentStep / 40;
    camera.position.z = THREE.MathUtils.lerp(startCameraZ, targetCameraZ, zoomProgress);
    
    updateLegsVisibility(glassesModel, true);
  } else if (currentStep < 60) {
    animationPhaseRef.current = 'pause';
    camera.position.z = 50;
    glassesModel.rotation.y = 0;
  } else if (currentStep < 70) {
    animationPhaseRef.current = 'rotate';
    const rotateProgress = (currentStep - 60) / 10;
    
    const startRotationY = 0;
    const targetRotationY = 22.5 * (Math.PI / 180);
    glassesModel.rotation.y = THREE.MathUtils.lerp(startRotationY, targetRotationY, rotateProgress);
    
    camera.position.z = THREE.MathUtils.lerp(50, 30, rotateProgress);
  } else if (currentStep < 110) {
    animationPhaseRef.current = 'slow_rotate';
    const rotateProgress = (currentStep - 70) / 40;
    
    const startRotationY = 22.5 * (Math.PI / 180);
    const targetRotationY = 33.75 * (Math.PI / 180);
    glassesModel.rotation.y = THREE.MathUtils.lerp(startRotationY, targetRotationY, rotateProgress);
    
    camera.position.z = 30;
    
    if (indicatorRef.current) {
      indicatorRef.current.style.opacity = '1';
      updateIndicatorPosition();
    }
  } else if (currentStep < 120) {
    animationPhaseRef.current = 'fast_rotate';
    const rotateProgress = (currentStep - 110) / 10;
    
    const startRotationY = 33.75 * (Math.PI / 180);
    const targetRotationY = 80 * (Math.PI / 180);
    glassesModel.rotation.y = THREE.MathUtils.lerp(startRotationY, targetRotationY, rotateProgress);
    
    camera.position.z = 30;
    glassesModel.position.z = THREE.MathUtils.lerp(0, 10, rotateProgress);
  } else if (currentStep < 150) {
    animationPhaseRef.current = 'separate';
    handleSeparate(currentStep, glassesModel, camera);
  } else if (currentStep < 160) {
    animationPhaseRef.current = 'pause_after_separate';
    camera.position.z = 30;
    glassesModel.rotation.y = 80 * (Math.PI / 180);
    
    handleSeparate(149, glassesModel, camera);
  } else if (currentStep < 200) {
    animationPhaseRef.current = 'restore';
    const restoreProgress = (currentStep - 160) / 40;
    handleRestore(restoreProgress, glassesModel, camera);
  } else if (currentStep < 220) {
    animationPhaseRef.current = 'show_right_side';
    handleShowRightSide(currentStep, glassesModel, camera);
  } else if (currentStep < 260) {
    animationPhaseRef.current = 'slow_right_side';
    handleSlowRightSide(currentStep, glassesModel, camera);
  } else if (currentStep < 280) {
    animationPhaseRef.current = 'show_back';
    handleShowBack(currentStep, glassesModel, camera);
  } else if (currentStep < 300) {
    animationPhaseRef.current = 'slow_back';
    handleSlowBack(currentStep, glassesModel, camera);
  } else if (currentStep < 320) {
    animationPhaseRef.current = 'reset';
    handleReset(currentStep, glassesModel, camera);
  } else if (currentStep < 340) {
    animationPhaseRef.current = 'end';
    handleEnd(glassesModel, camera);
  } else {
    animationPhaseRef.current = 'hide';
    handleHide(glassesModel, camera);
  }
  
  return animationPhaseRef.current;
}

function handleSeparate(currentStep, model, camera) {
  const separateProgress = (currentStep - 120) / 30;
  
  const leftRims = model.getObjectByName('Camera_Rims_Left');
  const leftLens = model.getObjectByName('Camera_Lens_Left');
  const leftInnerRims = model.getObjectByName('Inner_Rims_Left');
  const leftGlasses = model.getObjectByName('Camera_Glasses_Left');

  if (leftRims) {
    leftRims.position.z = THREE.MathUtils.lerp(0, 3, separateProgress);
  }

  if (leftInnerRims) {
    leftInnerRims.position.z = THREE.MathUtils.lerp(0, 4, separateProgress);
  }

  if (leftLens) {
    leftLens.position.z = THREE.MathUtils.lerp(0, 5, separateProgress);
  }

  if (leftGlasses) {
    leftGlasses.position.z = THREE.MathUtils.lerp(0, 6, separateProgress);
  }

  camera.position.z = 30;
  model.rotation.y = 80 * (Math.PI / 180);
}

function handleRestore(restoreProgress, model, camera) {
  const leftRims = model.getObjectByName('Camera_Rims_Left');
  const leftLens = model.getObjectByName('Camera_Lens_Left');
  const leftInnerRims = model.getObjectByName('Inner_Rims_Left');
  const leftGlasses = model.getObjectByName('Camera_Glasses_Left');

  if (leftRims) {
    leftRims.position.z = THREE.MathUtils.lerp(3, 0, restoreProgress);
  }

  if (leftInnerRims) {
    leftInnerRims.position.z = THREE.MathUtils.lerp(4, 0, restoreProgress);
  }

  if (leftLens) {
    leftLens.position.z = THREE.MathUtils.lerp(5, 0, restoreProgress);
  }

  if (leftGlasses) {
    leftGlasses.position.z = THREE.MathUtils.lerp(6, 0, restoreProgress);
  }

  camera.position.z = 30;
  
  const startRotationY = 80 * (Math.PI / 180);
  const targetRotationY = 0;
  model.rotation.y = THREE.MathUtils.lerp(startRotationY, targetRotationY, restoreProgress);
}

function handleShowRightSide(currentStep, glassesModel, camera) {
  const showRightSideProgress = (currentStep - 200) / 20;
  
  const targetRotationY = -60 * (Math.PI / 180);
  glassesModel.rotation.y = THREE.MathUtils.lerp(0, targetRotationY, showRightSideProgress);
  
  glassesModel.position.x = THREE.MathUtils.lerp(0, -6, showRightSideProgress);
  
  glassesModel.position.y = THREE.MathUtils.lerp(0, -2, showRightSideProgress);

  glassesModel.position.z = THREE.MathUtils.lerp(10, 17, showRightSideProgress);
  
  const targetRotationZ = 10 * (Math.PI / 180);
  glassesModel.rotation.z = THREE.MathUtils.lerp(0, targetRotationZ, showRightSideProgress);
  
  updateLegsVisibility(glassesModel, true);
  
  camera.position.z = 30;
}

function handleSlowRightSide(currentStep, glassesModel, camera) {
  const slowRightSideProgress = (currentStep - 220) / 40;
  
  const startRotationY = -60 * (Math.PI / 180);
  const targetRotationY = -70 * (Math.PI / 180);
  glassesModel.rotation.y = THREE.MathUtils.lerp(startRotationY, targetRotationY, slowRightSideProgress);
  
  glassesModel.position.z = 17;
  
  glassesModel.position.y = -2;

  glassesModel.position.x = -6;

  
  glassesModel.rotation.z = 10 * (Math.PI / 180);
  
  updateLegsVisibility(glassesModel, false);
  
  if (slowRightSideProgress >= 0.5) {
    glassesModel.traverse((child) => {
      if (child.name === 'logo_2') {
        child.visible = true;
      }
      if (child.name === 'logo_1') {
        child.visible = false;
      }
    });
  } else {
    glassesModel.traverse((child) => {
      if (child.name === 'logo_2') {
        child.visible = false;
      }
      if (child.name === 'logo_1') {
        child.visible = true;
      }
    });
  }
  
  camera.position.z = 30;
}

function handleShowBack(currentStep, glassesModel, camera) {
  const showTopProgress = (currentStep - 260) / 20;
  
  const startRotationY = -70 * (Math.PI / 180);
  const targetRotationY = -135 * (Math.PI / 180);
  glassesModel.rotation.y = THREE.MathUtils.lerp(startRotationY, targetRotationY, showTopProgress);
  
  const startRotationZ = 10 * (Math.PI / 180);
  const targetRotationZ = 5 * (Math.PI / 180);
  glassesModel.rotation.z = THREE.MathUtils.lerp(startRotationZ, targetRotationZ, showTopProgress);
  
  glassesModel.position.x = THREE.MathUtils.lerp(-6, 0, showTopProgress);
  
  glassesModel.position.y = THREE.MathUtils.lerp(-2, 0, showTopProgress);
  
  glassesModel.position.z = THREE.MathUtils.lerp(17, 5, showTopProgress);
  
  updateLegsVisibility(glassesModel, true);
  
  glassesModel.traverse((child) => {
    if (child.name === 'logo_2') {
      child.visible = true;
    }
    if (child.name === 'logo_1') {
      child.visible = false;
    }
  });
  
  camera.position.z = 30;
}

function handleSlowBack(currentStep, glassesModel, camera) {
  const showBackProgress = (currentStep - 280) / 20;
  
  const startRotationY = -135 * (Math.PI / 180);
  const targetRotationY = -140 * (Math.PI / 180);
  glassesModel.rotation.y = THREE.MathUtils.lerp(startRotationY, targetRotationY, showBackProgress);
  
  const startRotationZ = 5 * (Math.PI / 180);
  const targetRotationZ = 0 * (Math.PI / 180);
  glassesModel.rotation.z = THREE.MathUtils.lerp(startRotationZ, targetRotationZ, showBackProgress);
  
  glassesModel.position.x = 0;
  glassesModel.position.y = 0;
  glassesModel.position.z = 5;
  
  updateLegsVisibility(glassesModel, true);
  
  glassesModel.traverse((child) => {
    if (child.name === 'logo_2') {
      child.visible = true;
    }
  });
  
  camera.position.z = 30;
}

function handleReset(currentStep, glassesModel, camera) {
  const resetProgress = (currentStep - 320) / 20;
  glassesModel.rotation.y = THREE.MathUtils.lerp(0, -140 * (Math.PI / 180), resetProgress);

  glassesModel.position.z = THREE.MathUtils.lerp(5, 0, resetProgress);
  camera.position.z = 30;
}

function handleEnd(glassesModel, camera) {
  camera.position.z = 30;
  glassesModel.rotation.y = 0;

  glassesModel.position.z = 0;
  
  updateLegsVisibility(glassesModel, true);
  
  glassesModel.traverse((child) => {
    if (child.name === 'logo_2') {
      child.visible = false;
    }
  });
}

function handleHide(glassesModel, camera) {
  camera.position.z = 30;
  glassesModel.rotation.y = 0;
  glassesModel.position.z = 0;
  
  updateLegsVisibility(glassesModel, true);
}